import { useState } from "react";
import {
  authorTaskFromLanguage,
  regenerateReward,
  type FromLanguageResponse,
  type TaskSpec,
} from "../api";

interface TaskAuthorProps {
  onTaskAuthored?: (payload: { taskSpec: TaskSpec; rewardSource: string }) => void;
}

export default function TaskAuthor({ onTaskAuthored }: TaskAuthorProps) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FromLanguageResponse | null>(null);
  const [showModal, setShowModal] = useState(false);

  async function handleSubmit() {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authorTaskFromLanguage(prompt.trim());
      setResult(r);
      setShowModal(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to author task");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegenerate() {
    if (!result) return;
    setLoading(true);
    try {
      const reward = await regenerateReward(result.task_spec);
      setResult({ ...result, reward });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Regeneration failed");
    } finally {
      setLoading(false);
    }
  }

  function handleApprove() {
    if (!result?.reward.source) return;
    onTaskAuthored?.({
      taskSpec: result.task_spec,
      rewardSource: result.reward.source,
    });
    setShowModal(false);
    setPrompt("");
    setResult(null);
  }

  return (
    <div className="card">
      <h2 className="card-title">Describe a task</h2>
      <p className="card-meta" style={{ marginBottom: 16 }}>
        Plain English. Example: "pick the red block and place it on the blue tray."
      </p>

      <textarea
        className="form-textarea"
        placeholder="Pick the red block and place it on the blue tray..."
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
      />

      {error && (
        <div className="badge badge-danger" style={{ marginTop: 12 }}>{error}</div>
      )}

      <div style={{ marginTop: 16 }}>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading || !prompt.trim()}
        >
          {loading ? "Authoring..." : "Author task"}
        </button>
      </div>

      {showModal && result && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3 className="modal-title">Review generated task</h3>

            {/* Grounded objects */}
            {result.grounded_objects.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div className="form-label">Grounded objects</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {result.grounded_objects.map((o) => (
                    <span key={o.id} className="badge badge-info">
                      {o.label}{o.color ? ` (${o.color})` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Task spec summary */}
            <div style={{ marginBottom: 16 }}>
              <div className="form-label">Task</div>
              <div className="text-sm">
                <strong>{result.task_spec.task_type}</strong>
                {result.task_spec.primary_object && ` — ${result.task_spec.primary_object}`}
                {result.task_spec.secondary_object && ` → ${result.task_spec.secondary_object}`}
              </div>
              <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                {result.task_spec.success_criteria.length} success criterion(a),{" "}
                {result.task_spec.estimated_episode_length_steps} step episodes
              </div>
            </div>

            {/* Reward source */}
            <div style={{ marginBottom: 16 }}>
              <div className="form-label">
                Generated reward function{" "}
                {result.reward.accepted ? (
                  <span className="badge badge-success">accepted</span>
                ) : (
                  <span className="badge badge-danger">rejected</span>
                )}
              </div>
              <pre style={{
                background: "#1A1A1A",
                color: "#E5E2D8",
                padding: 14,
                borderRadius: 6,
                fontSize: 12,
                lineHeight: 1.5,
                fontFamily: "ui-monospace, SFMono-Regular, monospace",
                overflowX: "auto",
                maxHeight: 320,
              }}>
                {result.reward.source ?? "// Reward synthesis failed:\n// " +
                  result.reward.validation.errors.join("\n// ")}
              </pre>
              {result.reward.dry_run?.ok && (
                <div className="text-sm text-muted" style={{ marginTop: 6 }}>
                  Dry-run: {result.reward.dry_run.n_steps} steps, mean reward{" "}
                  {result.reward.dry_run.mean?.toFixed(3)}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={loading}>
                Cancel
              </button>
              <button className="btn btn-secondary" onClick={handleRegenerate} disabled={loading}>
                Regenerate
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApprove}
                disabled={loading || !result.reward.accepted}
              >
                Approve & use
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
